import { Badge, Layout, Menu, Tooltip } from "antd";
import type { ItemType, MenuItemType } from "antd/es/menu/interface";
import { type IFlagsmithFeature, useFlags } from "flagsmith/react";
import { useCloseTrackerBadge } from "hooks/useCloseTrackerBadge";
import { useCompanies } from "hooks/useCompanies";
import { useHidePmCloseTabs } from "hooks/useHidePmCloseTabs";
import { usePluginAccess } from "hooks/usePluginAccess";
import { useSidebarCompanyId } from "hooks/useSidebarCompanyId";
import { useUser } from "hooks/useUser";
import { useWorkflowRuns } from "hooks/useWorkflowRuns";
import {
	ArrowLeftRight,
	CalendarCheck,
	ClipboardList,
	Download,
	FileInput,
	Folder,
	FolderOpen,
	LayoutGrid,
	Users,
} from "lucide-react";
import { isCompanyProjectsTab } from "pages/project-management/companyProjectsLink";
import type React from "react";
import { useContext, useEffect, useRef } from "react";
import { Link, useLocation, useMatch } from "react-router-dom";
import { config } from "utils/config";
import { analytics } from "../utils/analytics";
import { SIDER_MENU_INLINE_INDENT } from "./siderLayout";

/** Nav-row icon geometry, from the EQU-1120 spec. */
const ICON = { size: 18, strokeWidth: 1.8 } as const;

/**
 * The WORKSPACE section of the sidebar: everything that is not scoped to the
 * selected company (EQU-1120).
 *
 * What left this list in EQU-1120, all of it to somewhere that still exists:
 *
 * - Account, Support and Log out are now the user menu at the bottom of the
 *   sidebar (`SidebarUserButton`), with the same three actions.
 * - Help is gone from the nav. The `/help` route is untouched and the header's
 *   help menu still opens it ("Help centre").
 * - The Admin block is gone from the nav. Every `/admin/*` route is still
 *   registered in `Routes.tsx` and still scope-guarded server-side, so a
 *   staff member reaches the console by URL — there is no other link to it in
 *   the app. Called out on EQU-1120; reverting this file restores the block.
 */
export function AppMenu() {
	const { user } = useUser();
	const { runs } = useWorkflowRuns();

	const myApprovalsCount = user
		? runs.filter(
				(r) =>
					r.status === "RUNNING" &&
					r.metadata?.stepAssignees &&
					Object.values(r.metadata.stepAssignees).includes(user.id),
			).length
		: 0;

	const match = useMatch("/*");
	const fullPath = match?.params["*"];
	const location = useLocation();
	const path = fullPath?.split("/")?.[0];
	const { isPluginDisabled } = usePluginAccess();
	const flags = useFlags([
		"disable_workflow_templates",
		"disable_workflows",
		"hide_dashboard",
		"hide_intercompany",
	]);

	// `hide_dashboard` hides the close: the entry below, and — since
	// `CloseTrackerGate` — the `/workflow` route behind it. So the badge is
	// fetched only while the close is actually reachable; counting sign-offs
	// nobody can open is a `close/my-week` request for nothing.
	// A flag Flagsmith never returns reads as `undefined`, which falls through
	// to visible — same fail-open direction as the route gate.
	const isCloseHidden = !!(flags.hide_dashboard as IFlagsmithFeature)?.enabled;
	const closeTrackerBadge = useCloseTrackerBadge(!isCloseHidden);
	// Inverse too, paired with `IntercompanyGate` on the route.
	const isIntercompanyHidden = !!(flags.hide_intercompany as IFlagsmithFeature)
		?.enabled;
	// The release switch for the close inside Project Management. ON (the
	// default) keeps the separate Close Tracker group below exactly as it is.
	const isPmCloseTabsHidden = useHidePmCloseTabs();
	const companies = useCompanies();
	const sidebarCompanyId = useSidebarCompanyId();
	const hasNoCompanies = companies.data?.length === 0;

	// Use useRef to track component mount state
	const isMounted = useRef(true);

	// Set isMounted to false when component unmounts
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Create base menu children for workflow
	const workflowChildren: ItemType<MenuItemType>[] = [
		{
			label: <Link to="/workflow/projects">Projects</Link>,
			key: "workflow/projects",
		},
		{
			label: <Link to="/workflow/tasks">Tasks</Link>,
			key: "workflow/tasks",
		},
	];

	// Add Templates to workflowChildren if not disabled
	if (!(flags.disable_workflow_templates as IFlagsmithFeature)?.enabled) {
		workflowChildren.push({
			label: <Link to="/workflow/templates">Templates</Link>,
			key: "workflow/templates",
		});
	}

	// Add Workflows to workflowChildren if not disabled
	if (!(flags.disable_workflows as IFlagsmithFeature)?.enabled) {
		workflowChildren.push({
			label: <Link to="/workflow/workflows">Workflows</Link>,
			key: "workflow/workflows",
		});
	}

	// The Sider's railed state (collapsed or narrow), as antd's Menu reads it.
	const isSiderCollapsed = Boolean(
		useContext(Layout._InternalSiderContext).siderCollapsed,
	);

	// EQ-2473: with the close inside Project Management, the entry is a single
	// link to the Close board with no dropdown. The /workflow tab bar reaches
	// Projects, Tasks, Templates and Workflows. The board writes its own
	// default `?period=`, so the link carries none. The close attention count
	// moves here from the old Close board child; approvals stay on the icon.
	//
	// EQU-1120 renamed it All Projects. With `hide_pm_close_tabs` still ON it
	// keeps its dropdown: those four tabs are reachable from the page itself,
	// but only once you are on one of them, and that release has not shipped —
	// retiring the dropdown there too would hide Tasks, Templates and Workflows
	// from every account until it does.
	const allProjectsItem: ItemType<MenuItemType> = isPmCloseTabsHidden
		? {
				label: <div style={menuLinkStyle}>All Projects</div>,
				key: "workflow",
				icon: (
					<Badge count={myApprovalsCount} size="small" offset={[4, -2]}>
						<Folder {...ICON} />
					</Badge>
				),
				children: workflowChildren,
			}
		: {
				// A bare "3" means nothing read aloud: the link says what the count
				// is, and the badge is the sighted copy of it.
				label: (
					<Link
						to="/workflow"
						style={menuLinkStyle}
						aria-label={
							!isCloseHidden && closeTrackerBadge > 0
								? `All Projects, ${closeTrackerBadge} waiting for you`
								: undefined
						}
					>
						All Projects
					</Link>
				),
				key: "workflow",
				icon: (
					<Badge count={myApprovalsCount} size="small" offset={[4, -2]}>
						<Folder {...ICON} />
					</Badge>
				),
				// Not on the rail: beside a hidden label the count wraps the row and
				// pushes the icon out of it. The approvals badge stays on the icon.
				extra:
					isCloseHidden || isSiderCollapsed ? undefined : (
						<span aria-hidden="true">
							<Badge count={closeTrackerBadge} size="small" />
						</span>
					),
			};

	// Inter-company was the last child of the merged group and has no tab on
	// /workflow, so without the dropdown it is an entry of its own.
	const intercompanyItems: ItemType<MenuItemType>[] =
		isPmCloseTabsHidden || isIntercompanyHidden
			? []
			: [
					{
						label: (
							<Link to="/firm/intercompany" style={menuLinkStyle}>
								Inter-company
							</Link>
						),
						key: "firm-intercompany",
						icon: <ArrowLeftRight {...ICON} />,
						title: "Inter-company",
					},
				];

	const workspaceChildren: ItemType<MenuItemType>[] = [
		{
			label: <Link to="/">All Companies</Link>,
			key: "companies",
			icon: <LayoutGrid {...ICON} />,
			title: "All Companies",
		},
		// EQ-2362: directly below All Companies, directly above All Projects.
		...(!isCloseHidden && isPmCloseTabsHidden
			? [
					{
						// EQ-2311: a group header, not a link. The tracker itself is
						// reached through the Dashboard child below.
						label: <div style={menuLinkStyle}>Close Tracker</div>,
						key: "firm",
						// Badged on the icon so the count survives a railed sidebar.
						icon: (
							<Badge count={closeTrackerBadge} size="small" offset={[4, -2]}>
								<CalendarCheck {...ICON} />
							</Badge>
						),
						children: [
							{
								label: (
									<Link
										to="/workflow"
										// The company panel above has its own Analytics; this
										// name keeps the two links distinct for assistive tech.
										aria-label="Close Tracker dashboard"
									>
										Dashboard
									</Link>
								),
								key: "firm-dashboard",
								icon: <CalendarCheck {...ICON} />,
							},
							...(isIntercompanyHidden
								? []
								: [
										{
											label: <Link to="/firm/intercompany">Inter-company</Link>,
											key: "firm-intercompany",
											icon: <ArrowLeftRight {...ICON} />,
										},
									]),
							{
								// EQ-2317. Keyed by its path so `selected` below resolves
								// it like any other item. Not the All Projects "Templates"
								// above: those are project templates.
								label: (
									<Link
										to="/workflow/close/templates"
										aria-label="Close templates"
									>
										Templates
									</Link>
								),
								key: "workflow/close/templates",
								icon: <ClipboardList {...ICON} />,
							},
						],
					},
				]
			: []),
		allProjectsItem,
		...intercompanyItems,
		// A user with no companies gets no company panel at all, so the file
		// converter — the one tool they can use before connecting anything —
		// would otherwise have no entry anywhere. Its company-scoped twin lives
		// under BOOKKEEPING TOOLS; this is the standalone `/file-converter` page.
		...(hasNoCompanies
			? [
					{
						label: <Link to="/file-converter">File Converter</Link>,
						key: "file-converter",
						icon: <FileInput {...ICON} />,
						title: "File Converter",
					},
				]
			: []),
		{
			label: <Link to="/team">Team Management</Link>,
			key: "team",
			icon: <Users {...ICON} />,
			title: "Team Management",
		},
		{
			label: <Link to="/document-center">Document Center</Link>,
			key: "document-center",
			icon: <FolderOpen {...ICON} />,
			title: "Document Center",
		},
	];

	// Download Plug-in is always visible and visually emphasised (EQU-1120): the
	// plug-in is the step a new account most often stalls on, so it is the one
	// row in the sidebar that reads as green at rest.
	if (!isPluginDisabled) {
		workspaceChildren.push({
			label: (
				<Tooltip title="Our plug-in is supported on Chrome, Edge, or Brave. Please use one of these browsers for optimal functionality.">
					<a
						href={config.links.downloadPlugin}
						target="_blank"
						rel="noreferrer"
					>
						Download Plug-in
					</a>
				</Tooltip>
			),
			key: "download",
			icon: <Download {...ICON} />,
			className: "sider-item-emphasized",
			title: "Download Plug-in",
		});
	}

	const items: ItemType<MenuItemType>[] = [
		{
			type: "group",
			key: "section-workspace",
			label: "Workspace",
			children: workspaceChildren,
		},
	];

	const flatItems = workspaceChildren.flatMap((item: any) =>
		item?.children ? item?.children : item,
	);
	// 🪤 The close tracker lives at `/workflow`, but `flatItems` above replaces
	// any parent that HAS children with those children, and "workflow" is the
	// All Projects group, so no key ever matches the tracker's path and
	// NOTHING would highlight on it. Its entry is the Dashboard child
	// ("firm-dashboard") of the Close Tracker group.
	//
	// Resolve the close tracker's own two routes explicitly. Everything else
	// under /workflow is Project Management and still resolves by key, so with
	// the separate Close Tracker group `/workflow/projects` keeps highlighting
	// its own tab rather than the close. Without it (EQ-2473) All Projects
	// is one item keyed "workflow", which every /workflow page hits.
	// `workflow/close/*` is a client's close (the old address), part of the
	// tracker; it highlighted nothing. So did Inter-company: its key
	// "firm-intercompany" never equals its path "firm/intercompany" (/qa r9 E-8).
	const isCloseTracker =
		fullPath === "workflow" ||
		Boolean(fullPath?.startsWith("workflow/client")) ||
		(Boolean(fullPath?.startsWith("workflow/close/")) &&
			fullPath !== "workflow/close/templates");
	const isIntercompany = fullPath === "firm/intercompany";
	// Inside Project Management every close address is the board's, including
	// a client's close, close settings and the old My week `?view=week`, and
	// the board is the All Projects link itself (EQ-2473).
	const closeKey = isPmCloseTabsHidden ? "firm-dashboard" : "workflow";
	// The company panel's Company Projects row opened this Projects tab, for
	// the company still selected there, so that row is the highlighted one, not
	// All Projects (EQ-2488). The same test as CompanyMenu's, so exactly one of
	// the two menus highlights.
	const isCompanyProjects = isCompanyProjectsTab(location, sidebarCompanyId);
	const selected = isCompanyProjects
		? ""
		: isCloseTracker
			? closeKey
			: isIntercompany
				? "firm-intercompany"
				: path
					? flatItems.find(
							(item) => path === "" + item?.key || fullPath === "" + item?.key,
						)?.key || ""
					: "companies";

	// Only identify user if component is still mounted
	useEffect(() => {
		if (user && isMounted.current) {
			analytics.identify(String(user.id), {
				firstName: user?.firstName,
				lastName: user?.lastName,
				email: user?.email,
			});
		}
	}, [user]);

	// Open the group that holds the selected page on load, so a direct load or
	// reload of /workflow shows its highlighted Dashboard (/qa r12 A-1). Only
	// the initial value: after that the user opens and closes groups freely.
	// Not on a railed sider, where an open group is a popup left hanging open.
	const selectedGroup = isSiderCollapsed
		? undefined
		: workspaceChildren.find((item: any) =>
				item?.children?.some((child: any) => child?.key === selected),
			)?.key;

	return (
		<Menu
			mode="inline"
			className="sider-workspace-menu"
			inlineIndent={SIDER_MENU_INLINE_INDENT}
			items={items}
			selectedKeys={[selected as string]}
			defaultOpenKeys={selectedGroup ? [String(selectedGroup)] : undefined}
		/>
	);
}

/** Lets a long nav label wrap instead of being clipped by a narrow sidebar. */
const menuLinkStyle: React.CSSProperties = {
	display: "block",
	lineHeight: 1.3,
};
